import { api } from '@/api';
import type {
  AssignableDeviceQueryParams,
  AssignableDeviceResponse,
  TargetCompanyQueryParams,
  TargetCompanyResponse,
  AssignDevicesRequest,
  AssignDevicesResponse,
  AccessibleCompany,
} from '../types';

export async function fetchAssignableDevices(
  params: AssignableDeviceQueryParams
): Promise<AssignableDeviceResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page: params.page,
    pageSize: params.pageSize,
    search: params.search || undefined,
  };

  if (params.modelTypeIds && params.modelTypeIds.length > 0) {
    queryParams.modelTypeIds = params.modelTypeIds.join(',');
  }

  const result = await api.get<AssignableDeviceResponse>(
    `/v1/companies/${params.companyId}/assigned-devices`,
    { params: queryParams }
  );

  return result
}

export async function fetchAssignableDevicesCount(
  params: Omit<AssignableDeviceQueryParams, 'page' | 'pageSize'>
): Promise<{ total: number }> {
  const queryParams: Record<string, string | undefined> = {
    search: params.search || undefined,
  };

  if (params.modelTypeIds && params.modelTypeIds.length > 0) {
    queryParams.modelTypeIds = params.modelTypeIds.join(',');
  }

  return api.get<{ total: number }>(
    `/v1/companies/${params.companyId}/assigned-devices/count`,
    { params: queryParams }
  );
}

export async function fetchTargetCompanies(
  params: TargetCompanyQueryParams
): Promise<TargetCompanyResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page: params.page,
    pageSize: params.pageSize,
    search: params.search || undefined,
  };

  if (params.levels && params.levels.length > 0) {
    queryParams.levels = params.levels.join(',');
  }

  if (params.countryCodes && params.countryCodes.length > 0) {
    queryParams.countryCodes = params.countryCodes.join(',');
  }

  const result = await api.get<TargetCompanyResponse>(
    `/v1/companies/${params.sourceCompanyId}/companies`,
    { params: queryParams }
  );

  return result;
}

export async function fetchTargetCompaniesCount(
  params: Omit<TargetCompanyQueryParams, 'page' | 'pageSize'>
): Promise<{ total: number }> {
  const queryParams: Record<string, string | undefined> = {
    search: params.search || undefined,
  };

  if (params.levels && params.levels.length > 0) {
    queryParams.levels = params.levels.join(',');
  }

  if (params.countryCodes && params.countryCodes.length > 0) {
    queryParams.countryCodes = params.countryCodes.join(',');
  }

  return api.get<{ total: number }>(
    `/v1/companies/${params.sourceCompanyId}/companies/count`,
    { params: queryParams }
  );
}

/* export async function fetchExistingAssignments(
  params: ExistingAssignmentQueryParams
): Promise<ExistingAssignmentResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page: params.page,
    pageSize: params.pageSize,
  };

  return api.get<ExistingAssignmentResponse>(
    `/v1/companies/${params.companyId}/devices`,
    { params: queryParams }
  );
}

export async function fetchExistingAssignmentsCount(
  companyId: string
): Promise<{ total: number }> {
  return api.get<{ total: number }>(
    `/v1/companies/${companyId}/devices/count`
  );
}
 */
export async function assignDevices(
  request: AssignDevicesRequest
): Promise<AssignDevicesResponse> {
  return api.post<AssignDevicesResponse>('/v1/devices/assign', request);
}

export async function fetchAccessibleCompanies(
  companyId: string
): Promise<AccessibleCompany[]> {
  const companies = await fetchTargetCompanies({
    page: 1,
    pageSize: 100,
    sourceCompanyId: companyId,
  });
  return companies.data;
}

// Removing an assignment means moving the devices back to the company the user acts for.
// The backend has no dedicated remove endpoint, so this reuses the assign call.
export async function removeDeviceAssignment(
  deviceIds: string[],
  ownCompanyId: string
): Promise<AssignDevicesResponse> {
  return assignDevices({ deviceIds, targetCompanyId: ownCompanyId });
}
